<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import Konva from "konva";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import {
  showConfirmDialog,
  showLoadingToast,
  showSuccessToast,
  Popup,
  showImagePreview,
} from "vant";
import { base64ToFile } from "@/modules/common";
import useMoveAndSelection from "@/components/mobile/generation/custom-painting/useMoveAndSelection";
import { useMedia } from "@/stores/media";
import { useMate } from "@/stores/mate";
import api from "@/modules/api";
import Stage = Konva.Stage;
import Layer = Konva.Layer;
import Image = Konva.Image;
import { useTask } from "@/stores/task";
import i18n from "@/locales";
import { useGalGameEditor } from "@/stores/galGameEditor";
import { useRoute, useRouter } from "vue-router";
import { AddImageToStageInstance } from "../custom-painting/AddImageToStage.vue";
import { nextTick } from "vue";
import { StorageGet, StorageRemove, StorageSet } from "@/tools/localStorage";
import { exportImage } from "@/components/mobile/generation/util";

Konva.hitOnDragEnabled = true;
// Props

// State
const mediaStore = useMedia();
const mateStore = useMate();
const galGameEditorStore = useGalGameEditor();
const route = useRoute();
const isGalGame = ref(false);
const taskStore = useTask();
const router = useRouter();
const main = ref<HTMLDivElement | null>(null);
const stage = ref<Stage | null>(null);
const backgroundLayer = ref<Layer | null>(null); // 背景图片的图层
const layer = ref<Layer | null>(null); // 立绘图片的图层
const characterShapes = ref<Image[]>([]);
const backgroundShapes = ref<Image[]>([]);
const enableObjectMove = ref(true);

const bgImage = ref<AddImageToStageInstance>();
const roleImage = ref<AddImageToStageInstance>();

const { clearSelection, activeShapes, transformer } = useMoveAndSelection(
  stage,
  layer,
  enableObjectMove
);
const showStyleSelection = ref(false);
const selectedStyleId = ref(0);

// Lifecycle
const prepareKonva = () => {
  // 1. 加载图片，根据图片和容器确定画布的宽高
  // 获取main的宽高
  const { width } = main.value!.getBoundingClientRect();
  // 2. 初始化Konva
  stage.value = new Konva.Stage({
    container: "container",
    width,
    height: (9 * width) / 16,
    draggable: false,
  });
  const bgLayer = new Konva.Layer();
  stage.value?.add(bgLayer);
  backgroundLayer.value = bgLayer;
  const characterLayer = new Konva.Layer();
  stage.value?.add(characterLayer);
  layer.value = characterLayer;
};

onMounted(() => {
  const { type } = route.query;
  if (type && type === "game") {
    isGalGame.value = true;
  }
  const loading = showLoadingToast({
    message: i18n.global.t("Message.loading"),
  });
  prepareKonva();
  loading.close();
});

onBeforeUnmount(() => {
  resetStage();
});

const resetStage = () => {
  stage.value?.destroy();
  stage.value = null;
  layer.value = null;
  characterShapes.value = [];
};

// Event Handler
const clear = async () => {
  try {
    await showConfirmDialog({
      title: i18n.global.t("ConfirmModal.promptTitle"),
      message: i18n.global.t("ConfirmModal.clearTheCanvas"),
      confirmButtonText: i18n.global.t("Buttons.confirm"),
      cancelButtonText: i18n.global.t("Buttons.cancel"),
    });
    backgroundShapes.value.forEach((s) => s.remove());
    characterShapes.value.forEach((s) => s.remove());
    characterShapes.value = [];
    clearSelection();
    StorageRemove("galGameBgCache");
    StorageRemove("galGameRolesCache");
  } catch {
    // do nothing
  }
};

const exportImageAndUpload = async () => {
  const targetWidth = 1024;
  const targetHeight = 576;
  const file = exportImage(
    stage.value as Konva.Stage,
    targetWidth,
    targetHeight
  );
  return mediaStore.uploadMediaItem(
    { file, mediaId: 0 },
    mateStore.currentMate!.id,
    7
  );
};

const onSaveMateGallery = async () => {
  const loadingToast = showLoadingToast({
    message: i18n.global.t("Message.savingAlbum"),
  });
  try {
    const media = await exportImageAndUpload();
    await api.createOneMateMedia({ mediaId: media.mediaId, type: 1 });
    loadingToast.close();
    showSuccessToast(i18n.global.t("Message.saveSuccess"));
  } catch (e) {
    loadingToast.close();
  }
};

const onSaveGalGameDialog = async () => {
  const loadingToast = showLoadingToast({
    message: i18n.global.t("Message.savingDialog"),
  });
  try {
    const media = await exportImageAndUpload();
    StorageSet("galGameBgSaveCache", media);
    await galGameEditorStore.updateDialogImg(media.mediaId, media.url!);
    showSuccessToast(i18n.global.t("Message.saveSuccess"));
    router.back();
  } finally {
    loadingToast.close();
  }
};

const onCreateTask = async () => {
  const loadingToast = showLoadingToast({
    message: i18n.global.t("Message.creatingImageGenerationTask"),
  });
  try {
    const media = await exportImageAndUpload();
    await taskStore.createTask(
      {
        sourceId: media.mediaId,
        style: selectedStyleId.value,
        ratio: "16:9",
        from: "image",
      },
      false
    );
    loadingToast.close();
    showSuccessToast(i18n.global.t("Message.creationSuccessful"));
    showStyleSelection.value = false;
  } finally {
    loadingToast.close();
  }
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr_auto] overflow-hidden">
    <threads-nav :show-center="false" show-back>
      <template #right>
        <button
          v-if="isGalGame"
          class="btn btn-sm btn-primary"
          @click="onSaveGalGameDialog"
        >
          {{ $t("Buttons.sure") }}
        </button>
        <!-- <button
          v-if="isGalGame"
          class="btn btn-sm btn-primary "
          @click="test"
        >
          test
        </button> -->
        <template v-else>
          <button class="btn btn-sm btn-primary" @click="onSaveMateGallery">
            {{ $t("Buttons.savedAlbum") }}
          </button>
          <button
            class="btn btn-sm btn-primary"
            @click="showStyleSelection = true"
          >
            {{ $t("Buttons.brush") }}
          </button>
        </template>
      </template>
    </threads-nav>
    <div
      ref="main"
      class="overflow-hidden bg-neutral relative flex flex-col items-center justify-center"
    >
      <div id="container" class="bg-white"></div>
      <div class="flex gap-2 p-2">
        <add-image-to-stage
          v-if="stage && backgroundLayer"
          ref="bgImage"
          v-model:imageShapes="backgroundShapes"
          :draggable="false"
          :layer="backgroundLayer"
          :media-select-opt="{
            replace: true,
            enableCrop: true,
            cropAspectRatio: 16 / 9,
          }"
          :stage="stage"
          full-screen
        >
          <button class="btn btn-sm">
            <i class="iconify mingcute--pic-line" />
            {{
              backgroundShapes.length === 0
                ? $t("Buttons.selectBackgroundImage")
                : $t("Buttons.changeBackgroundImage")
            }}
          </button>
        </add-image-to-stage>
        <add-image-to-stage
          v-if="stage && layer"
          ref="roleImage"
          v-model:imageShapes="characterShapes"
          :layer="layer"
          :media-select-opt="{
            maxCount: 5,
            enablePortraitGallery: true,
            enableImageGenGallery: false,
            enableMateGallery: false,
          }"
          :stage="stage"
        >
          <button class="btn btn-sm">
            <i class="iconfont icon-image-portrait" />
            {{ $t("Buttons.selectCharacterImage") }}
          </button>
        </add-image-to-stage>
        <button
          v-show="characterShapes.length > 0 || backgroundShapes.length > 0"
          class="btn btn-sm"
          @click="clear"
        >
          <delete-icon class="icon-sm" />
          {{ $t("Buttons.clear") }}
        </button>
      </div>
      <stage-operation-panel
        v-if="stage && layer"
        v-model:active-shapes="activeShapes"
        v-model:shapes="characterShapes"
        :layer="layer"
        :stage="stage"
        :title="`${$t('Texts.characterImage')} (${characterShapes.length})`"
        :transformer="transformer"
        class="absolute bg-base-100 bottom-0 left-0 w-[20rem] h-40"
      />
      <popup
        v-model:show="showStyleSelection"
        class="grid grid-rows-[auto_1fr] h-[80%] overflow-hidden"
        position="bottom"
        teleport="body"
      >
        <threads-nav
          :go-back="
            () => {
              showStyleSelection = false;
            }
          "
          show-back
        >
          <template #center>
            <div></div>
          </template>
          <template #right>
            <button
              :disabled="!selectedStyleId"
              class="btn btn-primary btn-sm"
              @click="onCreateTask"
            >
              {{ $t("Buttons.sure") }}
            </button>
          </template>
        </threads-nav>
        <div class="p-2 h-full overflow-y-auto">
          <image-style-selection v-model:selected-style-id="selectedStyleId" />
        </div>
      </popup>
    </div>
  </div>
</template>
